import type { BroadJumpScatterPoint, PublicResult } from '../types/result';

interface BroadJumpScatterChartProps {
  points: BroadJumpScatterPoint[];
  professionalBenchmarks: PublicResult[];
}

const width = 640;
const height = 360;
const margin = { top: 24, right: 24, bottom: 52, left: 58 };

function paddedDomain(values: number[]): [number, number] {
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const padding = Math.max((maximum - minimum) * 0.08, 1);
  return [minimum - padding, maximum + padding];
}

function tickValues(minimum: number, maximum: number): number[] {
  return Array.from(
    { length: 5 },
    (_, index) => minimum + ((maximum - minimum) * index) / 4,
  );
}

function formatTick(value: number, range: number): string {
  return value.toFixed(range < 8 ? 1 : 0);
}

export function BroadJumpScatterChart({
  points,
  professionalBenchmarks,
}: BroadJumpScatterChartProps) {
  const professionalPoints = professionalBenchmarks.flatMap((benchmark) => {
    if (benchmark.heightInches === null || benchmark.broadJumpInches === null) {
      return [];
    }

    return [{
      athleteId: benchmark.externalId,
      athleteName: benchmark.benchmarkLabel ??
        [benchmark.firstName, benchmark.lastName].filter(Boolean).join(' '),
      heightInches: benchmark.heightInches,
      broadJumpInches: benchmark.broadJumpInches,
      isCurrentAthlete: false,
      isProfessional: true,
    }];
  });
  const chartPoints = [
    ...points.map((point) => ({ ...point, isProfessional: false })),
    ...professionalPoints,
  ];

  if (chartPoints.length === 0) {
    return (
      <p className="py-12 text-center text-sm font-bold text-aws-slate">
        Broad jump comparison data is not available.
      </p>
    );
  }

  const [minimumHeight, maximumHeight] = paddedDomain(
    chartPoints.map((point) => point.heightInches),
  );
  const [minimumJump, maximumJump] = paddedDomain(
    chartPoints.map((point) => point.broadJumpInches),
  );
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const xPosition = (value: number) =>
    margin.left + ((value - minimumHeight) / (maximumHeight - minimumHeight)) * plotWidth;
  const yPosition = (value: number) =>
    margin.top + plotHeight - ((value - minimumJump) / (maximumJump - minimumJump)) * plotHeight;
  const heightTicks = tickValues(minimumHeight, maximumHeight);
  const jumpTicks = tickValues(minimumJump, maximumJump);
  const layeredPoints = [
    ...chartPoints.filter((point) => !point.isCurrentAthlete),
    ...chartPoints.filter((point) => point.isCurrentAthlete),
  ];

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        role="img"
        aria-labelledby="broad-jump-chart-title broad-jump-chart-description"
      >
        <title id="broad-jump-chart-title">Broad jump results by athlete height</title>
        <desc id="broad-jump-chart-description">
          Each dot represents an event participant or professional athlete. Your result is
          highlighted.
        </desc>

        {heightTicks.map((tick) => (
          <g key={`height-${tick}`}>
            <line
              x1={xPosition(tick)}
              y1={margin.top}
              x2={xPosition(tick)}
              y2={margin.top + plotHeight}
              stroke="currentColor"
              className="text-indigo-100"
            />
            <text
              x={xPosition(tick)}
              y={margin.top + plotHeight + 20}
              textAnchor="middle"
              className="fill-aws-slate text-[11px]"
            >
              {formatTick(tick, maximumHeight - minimumHeight)}
            </text>
          </g>
        ))}
        {jumpTicks.map((tick) => (
          <g key={`jump-${tick}`}>
            <line
              x1={margin.left}
              y1={yPosition(tick)}
              x2={width - margin.right}
              y2={yPosition(tick)}
              stroke="currentColor"
              className="text-indigo-100"
            />
            <text
              x={margin.left - 10}
              y={yPosition(tick)}
              dy="0.35em"
              textAnchor="end"
              className="fill-aws-slate text-[11px]"
            >
              {formatTick(tick, maximumJump - minimumJump)}
            </text>
          </g>
        ))}

        <line
          x1={margin.left}
          y1={margin.top + plotHeight}
          x2={width - margin.right}
          y2={margin.top + plotHeight}
          stroke="currentColor"
          className="text-indigo-200"
        />
        <line
          x1={margin.left}
          y1={margin.top}
          x2={margin.left}
          y2={margin.top + plotHeight}
          stroke="currentColor"
          className="text-indigo-200"
        />

        {layeredPoints.map((point) => (
          <circle
            key={point.athleteId}
            cx={xPosition(point.heightInches)}
            cy={yPosition(point.broadJumpInches)}
            r={point.isCurrentAthlete ? 8 : point.isProfessional ? 7 : 5}
            fill={point.isCurrentAthlete ? '#ff9900' : point.isProfessional ? '#00a1c9' : '#5f45ba'}
            stroke={point.isCurrentAthlete ? '#232f3e' : 'white'}
            strokeWidth={point.isCurrentAthlete ? 3 : point.isProfessional ? 2 : 1.5}
          >
            <title>
              {point.isCurrentAthlete
                ? 'Your result'
                : `${point.isProfessional ? 'Pro: ' : ''}${point.athleteName}`}: {point.heightInches}
              {' in tall, '}
              {point.broadJumpInches} in jump
            </title>
          </circle>
        ))}

        <text
          x={margin.left + plotWidth / 2}
          y={height - 10}
          textAnchor="middle"
          className="fill-aws-slate text-[13px] font-bold"
        >
          Athlete height (in)
        </text>
        <text
          x={16}
          y={margin.top + plotHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90 16 ${margin.top + plotHeight / 2})`}
          className="fill-aws-slate text-[13px] font-bold"
        >
          Broad jump (in)
        </text>
      </svg>

      <div className="mt-2 flex flex-wrap items-center justify-center gap-5 text-xs font-bold text-aws-slate">
        <span className="flex items-center gap-2">
          <span className="size-2.5 rounded-full bg-qs-violet" />
          Participants
        </span>
        {professionalPoints.length > 0 && (
          <span className="flex items-center gap-2">
            <span className="size-3 rounded-full border-2 border-white bg-cyan-600" />
            Pro athletes
          </span>
        )}
        <span className="flex items-center gap-2">
          <span className="size-3 rounded-full border-2 border-qs-navy bg-aws-orange" />
          Your result
        </span>
      </div>
    </div>
  );
}
